import * as ngCore from '@angular/core'
import { isDeepEqual } from '@attain-apps/data-utils'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../../state'

const REQUIRE_SPONSORED_ACCOUNT_ASSIGNMENTS_ATTRIBUTE =
  ents.extra.quiver.resources.data.dimensions.spaceSurveyConfigurations.entities
    .spaceSurveyConfig.attributes
    .requireSponsoredAccountAssignmentsForSponsoredFunctionalUsages.RESOURCE
    .payload
const REQUIRE_FUNCTIONAL_USAGE_ASSIGNMENTS_ATTRIBUTE =
  ents.extra.quiver.resources.data.dimensions.spaceSurveyConfigurations.entities
    .spaceSurveyConfig.attributes
    .requireFunctionalUsageAssignmentsForSelectedAccounts.RESOURCE.payload
const ALLOW_USERS_TO_UNLOCK_ROOMS_DIRECTLY_ATTRIBUTE =
  ents.extra.quiver.resources.data.dimensions.spaceSurveyConfigurations.entities
    .spaceSurveyConfig.attributes.allowUsersToUnlockRoomsDirectly.RESOURCE
    .payload

/**
 * Component for configuring survey settings within a space. This component
 * allows users to view and edit survey configuration options, such as
 * functional usage requirements, and save their changes. It handles loading
 * states, error states, and ensures that the save button is only enabled when
 * there are unsaved changes that can be persisted.
 */
@ngCore.Component({
  imports: [
    ds.button.Component,
    ds.illustration.emptyState.Component,
    ds.input.checkbox.Component,
    ds.layout.page.Component,
    ds.layout.section.Component,
    ds.layout.widget.Component,
    ds.loader.Component,
  ],
  selector: 'atn-space-survey-configuration',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.full.functions.Collection} */
  private readonly _spaceFunctionsCollection = ngCore.inject(
    state.space.full.functions.Collection,
  )

  /** @see {@link state.space.surveyConfiguration.Feature} */
  public readonly surveyConfigurationFeature = ngCore.inject(
    state.space.surveyConfiguration.Feature,
  )
  /** @see {@link state.me.Feature} */
  public readonly meFeature = ngCore.inject(state.me.Feature)

  /**
   * Computed signal for the page title, which uses the name of the survey
   * configuration if available, or falls back to a default title if not.
   */
  public readonly titleSg = ngCore.computed(
    (config = this.surveyConfigurationFeature.selectors.config.asSignal()) =>
      config?.name ?? 'Survey Configuration',
  )

  /**
   * Label for the sponsored-account assignment checkbox, including the
   * currently sponsored Space Function codes that the setting applies to.
   */
  public readonly requireSponsoredAccountAssignmentsLabelSg = ngCore.computed(
    (
      spaceFunctions = this._spaceFunctionsCollection.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
    ) => {
      const funcCodes = spaceFunctions
        .filter(func => func.isSponsored)
        .map(func => func.code)

      return funcCodes.length > 0
        ? `${REQUIRE_SPONSORED_ACCOUNT_ASSIGNMENTS_ATTRIBUTE.name} (${funcCodes.join(', ')})`
        : REQUIRE_SPONSORED_ACCOUNT_ASSIGNMENTS_ATTRIBUTE.name
    },
  )

  /**
   * Label for the functional-usage assignment checkbox, including the Space
   * Function codes currently marked as requiring matching payroll.
   */
  public readonly requireFunctionalUsageAssignmentsLabelSg = ngCore.computed(
    (
      spaceFunctions = this._spaceFunctionsCollection.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
    ) => {
      const funcCodes = spaceFunctions
        .filter(func => func.isRequiredForMatchingPayroll)
        .map(func => func.code)

      return funcCodes.length > 0
        ? `${REQUIRE_FUNCTIONAL_USAGE_ASSIGNMENTS_ATTRIBUTE.name} (${funcCodes.join(', ')})`
        : REQUIRE_FUNCTIONAL_USAGE_ASSIGNMENTS_ATTRIBUTE.name
    },
  )

  /**
   * Label for the unlock-rooms tools checkbox.
   */
  public readonly allowUsersToUnlockRoomsDirectlyLabel =
    ALLOW_USERS_TO_UNLOCK_ROOMS_DIRECTLY_ATTRIBUTE.name

  /**
   * Determines whether the save button should be enabled based on whether there
   * are unsaved changes, whether the config is still loading, or if there is an
   * error state that needs to be resolved.
   */
  public readonly canSaveSg = ngCore.computed(
    (
      config = this.surveyConfigurationFeature.selectors.config.asSignal(),
      editedConfig = this.surveyConfigurationFeature.selectors.editedConfig.asSignal(),
      error = this.surveyConfigurationFeature.selectors.error.asSignal(),
      isLoading = this.surveyConfigurationFeature.selectors.isLoading.asSignal(),
    ) => {
      if (isLoading || error || !editedConfig) {
        return false
      }

      return !isDeepEqual(config, editedConfig)
    },
  )

  /**
   * Updates the currently edited functional-usage configuration values as they
   * are changed in the form
   */
  public functionalUsageChangedHandler(
    updates: Partial<ents.extra.space.SurveyConfiguration.FunctionalUsage>,
  ): void {
    this.surveyConfigurationFeature.actions.updateFunctionalUsage
      .from('Space.SurveyConfiguration.FunctionalUsage.Changed')
      .dispatch(updates)
  }

  /**
   * Updates the currently edited survey-tools configuration values as they
   * are changed in the form
   */
  public toolsChangedHandler(
    updates: Partial<ents.extra.space.SurveyConfiguration.Tools>,
  ): void {
    this.surveyConfigurationFeature.actions.updateTools
      .from('Space.SurveyConfiguration.Tools.Changed')
      .dispatch(updates)
  }

  /**
   * Dispatches the upsert action to persist the currently edited survey
   * configuration
   */
  public saveClickHandler(): void {
    this.surveyConfigurationFeature.actions.upsert
      .from('Space.SurveyConfiguration.Save.Clicked')
      .dispatch()
  }
}
